import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { ArrowRight, CheckCircle2, Clock, Shield, MapPin, Car, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import Seo, { organizationJsonLd, websiteJsonLd } from "../components/Seo";

const HERO_IMG = "https://images.unsplash.com/photo-1625690180114-5530b1304127?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjYXIlMjBpbnRlcmlvciUyMHN0ZWVyaW5nJTIwd2hlZWx8ZW58MHx8fHwxNzgwNzc3ODY2fDA&ixlib=rb-4.1.0&q=85";
const SUCCESS_IMG = "https://images.pexels.com/photos/7144214/pexels-photo-7144214.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const { t } = useI18n();
  const [fb, setFb] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (sending || sent) return;
    setSending(true);
    try {
      await api.post("/feedback", fb);
      setSent(true);
      setFb({ name: "", email: "", message: "" });
      toast.success("Thanks — we'll get back to you within 1 business day.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="landing-page">
      <Seo
        title="DriveTerrain – Find Verified Driving Instructors in Canada"
        description="Book verified driving instructors across Canada. Learn stick or auto, pass your road test, and pay securely in CAD. Serving all 13 provinces & territories."
        keywords="driving school canada, driving instructors, road test prep, driving lessons, learners licence, G1 G2 test, class 5, driving lessons ontario, quebec, alberta, bc"
        canonical="/"
        ogType="website"
        ogImageAlt="DriveTerrain – Canada's driving instructor marketplace"
        jsonLd={[organizationJsonLd, websiteJsonLd]}
      />
      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{t("landing.tag")}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-display font-semibold tracking-tight leading-[1.02]">
              {t("landing.h1.a")} <span className="text-primary">{t("landing.h1.b")}</span><br/>{t("landing.h1.c")}
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">{t("landing.sub")}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/trainers"><Button size="lg" className="rounded-sm h-12 px-6" data-testid="hero-find-trainer-btn">{t("landing.findCta")} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/register?role=trainer"><Button size="lg" variant="outline" className="rounded-sm h-12 px-6" data-testid="hero-become-trainer-btn">{t("landing.becomeCta")}</Button></Link>
            </div>
            <div className="flex flex-wrap gap-6 pt-6 text-sm text-muted-foreground">
              {[
                { i: <Shield className="w-4 h-4" />, t: "Verified Canadian trainers" },
                { i: <Clock className="w-4 h-4" />, t: "OTP-based session start" },
                { i: <CheckCircle2 className="w-4 h-4" />, t: "Transparent CAD pricing" },
              ].map((x) => (
                <div key={x.t} className="flex items-center gap-2"><span className="text-primary">{x.i}</span>{x.t}</div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] relative overflow-hidden rounded-sm border border-border">
              <img src={HERO_IMG} alt="Steering wheel" className="absolute inset-0 w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-card border border-border p-5 max-w-[260px] hidden md:block">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{t("landing.coverage")}</div>
              <div className="text-2xl font-display font-semibold mt-1">{t("landing.provinces")}</div>
              <div className="text-sm text-muted-foreground">{t("landing.zones")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid md:grid-cols-12 gap-6">
            <div className="md:col-span-5 space-y-2">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">How it works</div>
              <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">From search to certified driver, in three precise steps.</h2>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-6">
              {[
                { n: "01", t: "Search by zone", d: "Filter trainers by city zone, transmission preference, and car type.", i: <MapPin className="w-5 h-5" /> },
                { n: "02", t: "Book & pay", d: "One-time session or a multi-hour package. Pay upfront via secure checkout.", i: <Car className="w-5 h-5" /> },
                { n: "03", t: "Start with OTP", d: "Trainer verifies a 6-digit OTP to start the session. Tamper-proof.", i: <Shield className="w-5 h-5" /> },
                { n: "04", t: "Overtime billed fairly", d: "Session timer auto-calculates overtime at the trainer's hourly rate.", i: <Clock className="w-5 h-5" /> },
              ].map((f) => (
                <div key={f.n} className="border border-border bg-card p-6 hover:-translate-y-1 transition-all duration-200">
                  <div className="text-primary">{f.i}</div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mt-3">{f.n}</div>
                  <div className="font-display text-xl font-medium mt-1">{f.t}</div>
                  <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SPLIT SECTION */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative aspect-square overflow-hidden border border-border">
            <img src={SUCCESS_IMG} alt="Happy learner" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="space-y-6">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">For trainers</div>
            <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">Set your rate. Define your packages. Get paid.</h2>
            <p className="text-muted-foreground leading-relaxed">
              List manual & automatic support, choose your service zone, and design learning packages.
              We handle payments, scheduling, and overtime billing — you focus on teaching.
            </p>
            <Link to="/register?role=trainer"><Button size="lg" className="rounded-sm" data-testid="cta-become-trainer">Open a trainer account <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </section>

      {/* Combined Feedback + Signup CTA — one pane, two columns */}
      <section className="border-b border-border bg-muted/20" id="feedback">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Left: Get in touch */}
            <div data-testid="feedback-column">
              <div className="text-xs uppercase tracking-[0.25em] text-primary font-bold">Get in touch</div>
              <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-2">Have a question or feedback?</h2>
              <p className="text-muted-foreground mt-3">
                Whether you&apos;re a learner, an instructor, or a partner — drop us a line and we&apos;ll reply within 1 business day.
              </p>
              {sent ? (
                <div className="mt-8 border border-primary/40 bg-primary/5 p-6 rounded-sm" data-testid="feedback-thankyou">
                  <div className="text-sm font-medium">Message received ✓</div>
                  <p className="text-sm text-muted-foreground mt-1">Thanks for reaching out. We&apos;ll be in touch shortly at the email you provided.</p>
                  <Button variant="outline" size="sm" className="rounded-sm mt-4" onClick={() => setSent(false)} data-testid="feedback-send-another">Send another</Button>
                </div>
              ) : (
                <form onSubmit={submitFeedback} className="mt-8 space-y-4" data-testid="feedback-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input placeholder="Your name" value={fb.name} onChange={(e) => setFb({ ...fb, name: e.target.value })} required className="rounded-sm h-11" data-testid="feedback-name" />
                    <Input type="email" placeholder="you@example.com" value={fb.email} onChange={(e) => setFb({ ...fb, email: e.target.value })} required className="rounded-sm h-11" data-testid="feedback-email" />
                  </div>
                  <Textarea placeholder="What is on your mind?" value={fb.message} onChange={(e) => setFb({ ...fb, message: e.target.value })} required rows={5} minLength={10} className="rounded-sm" data-testid="feedback-message" />
                  <Button type="submit" size="lg" className="rounded-sm h-12 px-8" disabled={sending} data-testid="feedback-submit">
                    {sending ? "Sending…" : (<><Send className="w-4 h-4 mr-2" />Send message</>)}
                  </Button>
                  <p className="text-xs text-muted-foreground">We&apos;ll only use your email to reply to you.</p>
                </form>
              )}
            </div>

            {/* Divider (desktop only) */}
            <div className="hidden lg:block absolute" aria-hidden="true"></div>

            {/* Right: Ready to take the wheel? */}
            <div className="lg:pl-16 lg:border-l border-border" data-testid="cta-column">
              <div className="text-xs uppercase tracking-[0.25em] text-primary font-bold">Ready to start</div>
              <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-2">Ready to take the wheel?</h2>
              <p className="text-muted-foreground mt-3">
                Book a lesson with a verified Canadian driving instructor, or sign up free and be behind the wheel this week.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/trainers" className="sm:flex-1"><Button size="lg" className="rounded-sm h-12 px-8 w-full" data-testid="cta-find-trainer">Find a trainer <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
                <Link to="/register" className="sm:flex-1"><Button size="lg" variant="outline" className="rounded-sm h-12 px-8 w-full" data-testid="cta-sign-up">Sign up free</Button></Link>
              </div>
              <ul className="mt-8 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> KYC-verified instructors across all 13 provinces &amp; territories</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> Secure CAD payments — pay only after your lesson is confirmed</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> Free to join, no monthly fees</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
